import type { GameState, Lane } from "@mmotdg/shared";
import * as THREE from "three";
import type { LaneTransform } from "./LaneTransform.js";

// Slice tint applied only to the grid lines so each player's territory is
// differentiable at a glance without dominating the scene.
const BOSS_SLICE_PALETTE = [
  0x6ee7ff, // cyan
  0xff8ace, // pink
  0xffb273, // peach
  0x9ce5a8, // mint
  0xffd07a, // gold
  0xb8a4ff, // lavender
];

function bossSliceColor(laneId: string): number {
  let h = 0;
  for (let i = 0; i < laneId.length; i++) h = (h * 31 + laneId.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % BOSS_SLICE_PALETTE.length;
  return BOSS_SLICE_PALETTE[idx]!;
}

export interface MapRendererSlice {
  laneId: string;
  yMin: number;
  yMax: number;
}

export interface MapRendererOptions {
  slice?: MapRendererSlice;
  transform?: LaneTransform;
}

interface Pulse {
  mesh: THREE.Mesh;
  start: number;
  duration: number;
  maxScale: number;
  initialOpacity: number;
}

interface PathPulse {
  ring: THREE.Mesh;
  start: number;
  duration: number;
}

interface SpawnDecal {
  ring: THREE.Mesh;
  glow: THREE.Mesh;
}

const HIT_SPARK_MS = 360;
const SPAWN_PULSE_MS = 480;
const BASE_PULSE_MS = 520;

export class MapRenderer {
  private readonly group = new THREE.Group();
  private readonly pathGroup = new THREE.Group();
  private readonly fxGroup = new THREE.Group();
  private readonly ownedGeometries: THREE.BufferGeometry[] = [];
  private readonly ownedMaterials: THREE.Material[] = [];
  private highlightMesh: THREE.Mesh | null = null;
  private highlightCellKey: string | null = null;
  private rangePreview: THREE.Mesh | null = null;
  private pathMaterial: THREE.LineDashedMaterial | null = null;
  private gridLines: THREE.LineSegments | null = null;
  private readonly activePulses: Pulse[] = [];
  private readonly spawnDecals: SpawnDecal[] = [];
  private readonly baseMeshes: THREE.Mesh[] = [];

  private readonly slice: MapRendererSlice | null;
  private readonly transform: LaneTransform | null;
  private playMaterial: THREE.MeshStandardMaterial | null = null;
  private gridMaterial: THREE.LineBasicMaterial | null = null;
  private spawnRingMaterial: THREE.MeshBasicMaterial | null = null;
  private spawnGlowMaterial: THREE.MeshBasicMaterial | null = null;
  private baseMaterial: THREE.MeshStandardMaterial | null = null;
  private basePadMaterial: THREE.MeshBasicMaterial | null = null;
  private sliceAlive = true;
  private readonly basePlayColor = new THREE.Color(0x1d2640);
  private readonly basePlayEmissive = new THREE.Color(0x000000);
  private readonly baseGridColor = new THREE.Color(0x6ee7ff);
  private readonly baseSpawnRing = new THREE.Color(0xff5e6b);
  private readonly baseBaseColor = new THREE.Color(0x5dd39e);

  constructor(
    private readonly target: THREE.Object3D,
    state: GameState,
    options?: MapRendererOptions,
  ) {
    this.slice = options?.slice ?? null;
    this.transform = options?.transform ?? null;
    this.target.add(this.group);
    this.target.add(this.pathGroup);
    this.target.add(this.fxGroup);
    this.buildGround(state);
    this.buildGrid(state);
    this.buildObstacles(state);
    this.buildLanesAndBases(state);
    this.buildHighlight();
    this.buildRangePreview();
    this.updatePaths(state);
  }

  // Maps (cx, cy) → world XZ. With a polar transform present we curve the
  // grid into a pie-slice; otherwise we use flat identity.
  private toWorld(flatX: number, flatY: number): { x: number; z: number } {
    if (this.transform) return this.transform.flatToWorld(flatX, flatY);
    return { x: flatX, z: flatY };
  }

  getTransform(): LaneTransform | null {
    return this.transform;
  }

  private inSlice(cy: number): boolean {
    if (!this.slice) return true;
    return cy >= this.slice.yMin && cy <= this.slice.yMax;
  }

  private allowedLane(lane: Lane): boolean {
    if (!this.slice) return true;
    return lane.id === this.slice.laneId;
  }

  private trackGeo<T extends THREE.BufferGeometry>(geo: T): T {
    this.ownedGeometries.push(geo);
    return geo;
  }

  private trackMat<T extends THREE.Material>(mat: T): T {
    this.ownedMaterials.push(mat);
    return mat;
  }

  private buildGround(state: GameState): void {
    const w = state.gridWidth;
    const h = state.gridHeight;
    const yMin = this.slice ? this.slice.yMin : 0;
    const yMax = this.slice ? this.slice.yMax : h - 1;
    const sliceHeight = yMax - yMin + 1;
    const sliceCenterY = (yMin + yMax + 1) / 2;

    if (this.transform) {
      const t = this.transform;
      // Annular sector with parallel-offset radial edges → uniform-width gap
      // between adjacent player slices. Shape's local Y is interpreted as
      // world Z (rotated to lay flat), so we pass sin(angle) directly.
      const gapWorld = 0; // gap comes from SLICE_FRACTION in LaneScene
      const sliceLeft = t.sliceCenterAngle - t.sliceAngularSpan / 2;
      const sliceRight = t.sliceCenterAngle + t.sliceAngularSpan / 2;
      const innerLeftA = sliceLeft + gapWorld / 2 / t.hubRadius;
      const innerRightA = sliceRight - gapWorld / 2 / t.hubRadius;
      const outerLeftA = sliceLeft + gapWorld / 2 / t.outerRadius;
      const outerRightA = sliceRight - gapWorld / 2 / t.outerRadius;

      const segs = 24;
      const shape = new THREE.Shape();
      shape.moveTo(t.hubRadius * Math.cos(innerLeftA), t.hubRadius * Math.sin(innerLeftA));
      // Inner arc: line segments from innerLeftA to innerRightA (CCW)
      for (let i = 1; i <= segs; i++) {
        const u = i / segs;
        const a = innerLeftA + u * (innerRightA - innerLeftA);
        shape.lineTo(t.hubRadius * Math.cos(a), t.hubRadius * Math.sin(a));
      }
      // Right edge: from inner-right to outer-right with varying angle
      for (let i = 1; i <= segs; i++) {
        const tt = i / segs;
        const r = t.hubRadius + tt * (t.outerRadius - t.hubRadius);
        const a = sliceRight - gapWorld / 2 / r;
        shape.lineTo(r * Math.cos(a), r * Math.sin(a));
      }
      // Outer arc: from outerRightA back to outerLeftA (CW)
      for (let i = 1; i <= segs; i++) {
        const u = i / segs;
        const a = outerRightA + u * (outerLeftA - outerRightA);
        shape.lineTo(t.outerRadius * Math.cos(a), t.outerRadius * Math.sin(a));
      }
      // Left edge: from outer-left back to inner-left with varying angle
      for (let i = 1; i <= segs; i++) {
        const tt = 1 - i / segs;
        const r = t.hubRadius + tt * (t.outerRadius - t.hubRadius);
        const a = sliceLeft + gapWorld / 2 / r;
        shape.lineTo(r * Math.cos(a), r * Math.sin(a));
      }
      shape.closePath();

      const playGeo = this.trackGeo(new THREE.ShapeGeometry(shape, 24));
      // +π/2 around X so that shape (x, y) ends up at world (x, 0, +y).
      // This keeps Z signs consistent with flatToWorld → mesh and ground
      // land in the same slice.
      playGeo.rotateX(Math.PI / 2);
      const playMat = this.trackMat(new THREE.MeshStandardMaterial({
        color: this.basePlayColor.clone(),
        roughness: 0.88,
        metalness: 0,
        side: THREE.DoubleSide,
      }));
      this.playMaterial = playMat;
      const play = new THREE.Mesh(playGeo, playMat);
      play.position.y = 0.005;
      play.receiveShadow = true;
      this.group.add(play);
      return;
    }

    const groundSize = this.slice ? Math.max(w + 4, sliceHeight + 4) : Math.max(w, h) * 20;

    const geo = this.trackGeo(new THREE.PlaneGeometry(groundSize, groundSize, 1, 1));
    geo.rotateX(-Math.PI / 2);
    geo.translate(w / 2, 0, sliceCenterY);
    const mat = this.trackMat(new THREE.MeshStandardMaterial({
      color: 0x141a28,
      roughness: 0.95,
      metalness: 0,
    }));
    const ground = new THREE.Mesh(geo, mat);
    ground.receiveShadow = true;
    this.group.add(ground);

    const playGeo = this.trackGeo(new THREE.PlaneGeometry(w, sliceHeight, 1, 1));
    playGeo.rotateX(-Math.PI / 2);
    playGeo.translate(w / 2, 0.005, sliceCenterY);
    const playMat = this.trackMat(new THREE.MeshStandardMaterial({
      color: 0x1d2640,
      roughness: 0.88,
      metalness: 0,
    }));
    const play = new THREE.Mesh(playGeo, playMat);
    play.receiveShadow = true;
    this.group.add(play);
  }

  private buildGrid(state: GameState): void {
    const w = state.gridWidth;
    const h = state.gridHeight;
    const yMin = this.slice ? this.slice.yMin : 0;
    const yMax = this.slice ? this.slice.yMax + 1 : h;
    const points: number[] = [];
    if (this.transform) {
      const t = this.transform;
      const gapWorld = 0; // gap comes from SLICE_FRACTION in LaneScene
      const sliceLeft = t.sliceCenterAngle - t.sliceAngularSpan / 2;
      const sliceRight = t.sliceCenterAngle + t.sliceAngularSpan / 2;
      const radialSegments = 24;
      const minVisibleA = sliceLeft + gapWorld / 2 / t.outerRadius + 0.0001;
      const maxVisibleA = sliceRight - gapWorld / 2 / t.outerRadius - 0.0001;
      // Radial lines: one per integer cy boundary, going from yMin (inner
      // band edge) all the way to yMax + 1 (outer band edge). Use the same
      // flatToWorld mapping the renderers do so the grid aligns with towers.
      for (let cy = yMin; cy <= yMax + 1; cy++) {
        const angle = t.worldAngleAt(cy);
        if (angle < minVisibleA || angle > maxVisibleA) continue;
        const rMinLeft = gapWorld / 2 / Math.max(0.0001, angle - sliceLeft);
        const rMinRight = gapWorld / 2 / Math.max(0.0001, sliceRight - angle);
        const r0 = Math.max(t.hubRadius, rMinLeft, rMinRight);
        const r1 = t.outerRadius;
        if (r0 >= r1) continue;
        points.push(Math.cos(angle) * r0, 0.012, Math.sin(angle) * r0);
        points.push(Math.cos(angle) * r1, 0.012, Math.sin(angle) * r1);
      }
      // Concentric arcs: one per integer cx boundary, using flatToWorld to
      // get the radius so it matches tower placement exactly.
      const probe = new THREE.Vector3();
      for (let cx = 1; cx <= w - 1; cx++) {
        const w0 = t.flatToWorld(cx, t.laneRow + 0.5);
        probe.set(w0.x, 0, w0.z);
        const radius = probe.length();
        const aLeft = sliceLeft + gapWorld / 2 / radius;
        const aRight = sliceRight - gapWorld / 2 / radius;
        if (aRight <= aLeft) continue;
        for (let i = 0; i < radialSegments; i++) {
          const u0 = i / radialSegments;
          const u1 = (i + 1) / radialSegments;
          const a0 = aLeft + u0 * (aRight - aLeft);
          const a1 = aLeft + u1 * (aRight - aLeft);
          points.push(Math.cos(a0) * radius, 0.012, Math.sin(a0) * radius);
          points.push(Math.cos(a1) * radius, 0.012, Math.sin(a1) * radius);
        }
      }
    } else {
      for (let i = 0; i <= w; i++) {
        points.push(i, 0.01, yMin, i, 0.01, yMax);
      }
      for (let j = yMin; j <= yMax; j++) {
        points.push(0, 0.01, j, w, 0.01, j);
      }
    }
    const geo = this.trackGeo(new THREE.BufferGeometry());
    geo.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    const gridColor = this.transform
      ? bossSliceColor(this.slice?.laneId ?? "")
      : 0x6ee7ff;
    this.baseGridColor.setHex(gridColor);
    const mat = this.trackMat(new THREE.LineBasicMaterial({
      color: this.baseGridColor.clone(),
      transparent: true,
      opacity: this.transform ? 0.35 : 0.08,
    }));
    this.gridMaterial = mat;
    this.gridLines = new THREE.LineSegments(geo, mat);
    this.group.add(this.gridLines);
  }

  private buildObstacles(state: GameState): void {
    if (state.obstacles.length === 0) return;
    const w = state.gridWidth;
    const h = state.gridHeight;
    const geo = this.trackGeo(new THREE.BoxGeometry(1, 1.2, 1));
    const mat = this.trackMat(new THREE.MeshStandardMaterial({
      color: 0x4a5570,
      roughness: 0.6,
      emissive: 0x1a2030,
      emissiveIntensity: 0.4,
    }));
    for (const o of state.obstacles) {
      const isFrame = o.cx === 0 || o.cx === w - 1 || o.cy === 0 || o.cy === h - 1;
      if (isFrame) continue;
      if (!this.inSlice(o.cy)) continue;
      const mesh = new THREE.Mesh(geo, mat);
      const pos = this.toWorld(o.cx + 0.5, o.cy + 0.5);
      mesh.position.set(pos.x, 0.6, pos.z);
      if (this.transform) mesh.rotation.y = -this.transform.worldAngleAt(o.cy + 0.5);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }
  }

  private buildLanesAndBases(state: GameState): void {
    if (state.lanes.length === 0) return;
    const spawnGeo = this.trackGeo(new THREE.RingGeometry(0.32, 0.46, 24));
    const spawnGlowGeo = this.trackGeo(new THREE.CircleGeometry(0.4, 24));
    const spawnMat = this.trackMat(new THREE.MeshBasicMaterial({
      color: this.baseSpawnRing.clone(),
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
    }));
    this.spawnRingMaterial = spawnMat;
    const spawnGlowMat = this.trackMat(new THREE.MeshBasicMaterial({
      color: this.baseSpawnRing.clone(),
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    this.spawnGlowMaterial = spawnGlowMat;
    const baseGeo = this.trackGeo(new THREE.BoxGeometry(0.9, 0.8, 0.9));
    const baseMat = this.trackMat(new THREE.MeshStandardMaterial({
      color: this.baseBaseColor.clone(),
      emissive: 0x2a6e54,
      emissiveIntensity: 0.55,
    }));
    this.baseMaterial = baseMat;
    const basePadGeo = this.trackGeo(new THREE.RingGeometry(0.55, 0.85, 24));
    const basePadMat = this.trackMat(new THREE.MeshBasicMaterial({
      color: this.baseBaseColor.clone(),
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    this.basePadMaterial = basePadMat;
    for (const lane of state.lanes) {
      if (!this.allowedLane(lane)) continue;
      const spawnPos = this.toWorld(lane.spawnCell.cx + 0.5, lane.spawnCell.cy + 0.5);
      const basePos = this.toWorld(lane.baseCell.cx + 0.5, lane.baseCell.cy + 0.5);

      const ring = new THREE.Mesh(spawnGeo, spawnMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(spawnPos.x, 0.03, spawnPos.z);
      this.group.add(ring);

      const glow = new THREE.Mesh(spawnGlowGeo, spawnGlowMat);
      glow.rotation.x = -Math.PI / 2;
      glow.position.set(spawnPos.x, 0.02, spawnPos.z);
      this.group.add(glow);
      this.spawnDecals.push({ ring, glow });

      const base = new THREE.Mesh(baseGeo, baseMat);
      base.position.set(basePos.x, 0.4, basePos.z);
      base.castShadow = true;
      this.group.add(base);
      this.baseMeshes.push(base);

      const pad = new THREE.Mesh(basePadGeo, basePadMat);
      pad.rotation.x = -Math.PI / 2;
      pad.position.set(basePos.x, 0.02, basePos.z);
      this.group.add(pad);
    }
  }

  private buildHighlight(): void {
    // Built lazily in highlightCell so we can shape it to the actual cell
    // (square in flat mode, polar wedge in disk mode).
  }

  private buildCellGeometry(cell: { cx: number; cy: number }): THREE.BufferGeometry {
    if (!this.transform) {
      const geo = new THREE.BoxGeometry(1, 0.05, 1);
      return geo;
    }
    const t = this.transform;
    const rIn = t.radiusAt(cell.cx);
    const rOut = t.radiusAt(cell.cx + 1);
    const aL = t.worldAngleAt(cell.cy);
    const aR = t.worldAngleAt(cell.cy + 1);
    const segs = 6;
    const shape = new THREE.Shape();
    shape.moveTo(rIn * Math.cos(aL), rIn * Math.sin(aL));
    for (let i = 1; i <= segs; i++) {
      const u = i / segs;
      const a = aL + u * (aR - aL);
      shape.lineTo(rIn * Math.cos(a), rIn * Math.sin(a));
    }
    shape.lineTo(rOut * Math.cos(aR), rOut * Math.sin(aR));
    for (let i = 1; i <= segs; i++) {
      const u = i / segs;
      const a = aR + u * (aL - aR);
      shape.lineTo(rOut * Math.cos(a), rOut * Math.sin(a));
    }
    shape.closePath();
    const geo = new THREE.ShapeGeometry(shape, 8);
    geo.rotateX(Math.PI / 2);
    return geo;
  }

  private buildRangePreview(): void {
    const geo = this.trackGeo(new THREE.RingGeometry(0.95, 1.0, 64));
    geo.rotateX(-Math.PI / 2);
    const mat = this.trackMat(new THREE.MeshBasicMaterial({
      color: 0x6ee7ff,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    this.fxGroup.add(mesh);
    this.rangePreview = mesh;
  }

  highlightCell(cell: { cx: number; cy: number } | null, valid: boolean): void {
    if (!cell) {
      if (this.highlightMesh) this.highlightMesh.visible = false;
      return;
    }
    const key = `${cell.cx},${cell.cy}`;
    if (this.highlightMesh && this.highlightCellKey === key) {
      // Same cell — only the validity colour might have changed.
      const mat = this.highlightMesh.material as THREE.MeshBasicMaterial;
      mat.color.setHex(valid ? 0x6ee7ff : 0xff5e6b);
      this.highlightMesh.visible = true;
      return;
    }
    // Different cell — rebuild geometry to match the cell's actual shape.
    if (this.highlightMesh) {
      this.target.remove(this.highlightMesh);
      this.highlightMesh.geometry.dispose();
    }
    const geo = this.buildCellGeometry(cell);
    let mat = this.highlightMesh?.material as THREE.MeshBasicMaterial | undefined;
    if (!mat) {
      mat = this.trackMat(new THREE.MeshBasicMaterial({
        color: 0x6ee7ff,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
        side: THREE.DoubleSide,
      }));
    }
    mat.color.setHex(valid ? 0x6ee7ff : 0xff5e6b);
    const mesh = new THREE.Mesh(geo, mat);
    if (this.transform) {
      mesh.position.y = 0.025;
    } else {
      mesh.position.set(cell.cx + 0.5, 0.025, cell.cy + 0.5);
    }
    this.target.add(mesh);
    this.highlightMesh = mesh;
    this.highlightCellKey = key;
  }

  showRangePreview(target: { x: number; y: number; range: number } | null): void {
    if (!this.rangePreview) return;
    if (!target) {
      this.rangePreview.visible = false;
      return;
    }
    this.rangePreview.visible = true;
    const w = this.toWorld(target.x, target.y);
    this.rangePreview.position.set(w.x, 0.04, w.z);
    this.rangePreview.scale.set(target.range, 1, target.range);
  }

  updatePaths(state: GameState): void {
    while (this.pathGroup.children.length > 0) {
      const child = this.pathGroup.children.pop()!;
      if ((child as THREE.Line).geometry) (child as THREE.Line).geometry.dispose();
    }
    if (!this.pathMaterial) {
      this.pathMaterial = new THREE.LineDashedMaterial({
        color: 0x9be8ff,
        transparent: true,
        opacity: 0.65,
        dashSize: 0.55,
        gapSize: 0.35,
      });
    }
    for (const lane of state.lanes) {
      if (!this.allowedLane(lane)) continue;
      if (lane.currentPath.length < 2) continue;
      const points = lane.currentPath.map((p) => {
        const w = this.toWorld(p.cx + 0.5, p.cy + 0.5);
        return new THREE.Vector3(w.x, 0.05, w.z);
      });
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, this.pathMaterial);
      line.computeLineDistances();
      this.pathGroup.add(line);
    }
  }

  spawnHitSpark(x: number, y: number, splashRadius: number): void {
    const radius = Math.max(0.18, splashRadius || 0.4);
    const geo = new THREE.RingGeometry(radius * 0.45, radius * 0.5, 24);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffd089,
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const _w = this.toWorld(x, y); mesh.position.set(_w.x, 0.18, _w.z);
    this.fxGroup.add(mesh);
    this.activePulses.push({
      mesh,
      start: performance.now(),
      duration: HIT_SPARK_MS,
      maxScale: 2.4,
      initialOpacity: 0.95,
    });
  }

  spawnPulse(x: number, y: number, yOffset: number): void {
    const geo = new THREE.RingGeometry(0.3, 0.42, 24);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff8a94,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const _w = this.toWorld(x, y);
    mesh.position.set(_w.x, 0.05 + yOffset, _w.z);
    this.fxGroup.add(mesh);
    this.activePulses.push({
      mesh,
      start: performance.now(),
      duration: SPAWN_PULSE_MS,
      maxScale: 3.0,
      initialOpacity: 0.85,
    });
  }

  basePulse(x: number, y: number): void {
    const geo = new THREE.RingGeometry(0.4, 0.55, 24);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff5e6b,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const _w = this.toWorld(x, y);
    mesh.position.set(_w.x, 0.05, _w.z);
    this.fxGroup.add(mesh);
    this.activePulses.push({
      mesh,
      start: performance.now(),
      duration: BASE_PULSE_MS,
      maxScale: 3.6,
      initialOpacity: 1,
    });
  }

  tick(time: number, _dt: number): void {
    if (this.pathMaterial) {
      this.pathMaterial.opacity = 0.55 + Math.sin(time * 2.2) * 0.18;
    }
    if (this.rangePreview) {
      const mat = this.rangePreview.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.55 + Math.sin(time * 4) * 0.2;
    }

    for (let i = 0; i < this.spawnDecals.length; i++) {
      const d = this.spawnDecals[i];
      const pulse = 1 + Math.sin(time * 2.4 + i * 0.7) * 0.12;
      d.ring.scale.set(pulse, pulse, pulse);
      const glowMat = d.glow.material as THREE.MeshBasicMaterial;
      glowMat.opacity = 0.25 + Math.sin(time * 3 + i) * 0.18;
    }

    const now = performance.now();
    for (let i = this.activePulses.length - 1; i >= 0; i--) {
      const p = this.activePulses[i];
      const k = (now - p.start) / p.duration;
      if (k >= 1) {
        this.fxGroup.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.activePulses.splice(i, 1);
        continue;
      }
      const scale = 1 + k * (p.maxScale - 1);
      p.mesh.scale.set(scale, 1, scale);
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = p.initialOpacity * (1 - k);
    }
  }

  setSliceAlive(alive: boolean): void {
    if (this.sliceAlive === alive) return;
    this.sliceAlive = alive;
    const grey = new THREE.Color(0x2a2e36);
    const greyGrid = new THREE.Color(0x4a505a);
    const greyDecal = new THREE.Color(0x555a64);
    if (this.playMaterial) {
      this.playMaterial.color.copy(alive ? this.basePlayColor : grey);
      this.playMaterial.emissive.copy(alive ? this.basePlayEmissive : new THREE.Color(0x000000));
      this.playMaterial.needsUpdate = true;
    }
    if (this.gridMaterial) {
      this.gridMaterial.color.copy(alive ? this.baseGridColor : greyGrid);
      this.gridMaterial.opacity = alive ? (this.transform ? 0.35 : 0.08) : 0.15;
      this.gridMaterial.needsUpdate = true;
    }
    if (this.spawnRingMaterial) {
      this.spawnRingMaterial.color.copy(alive ? this.baseSpawnRing : greyDecal);
      this.spawnRingMaterial.opacity = alive ? 0.95 : 0.4;
    }
    if (this.spawnGlowMaterial) {
      this.spawnGlowMaterial.color.copy(alive ? this.baseSpawnRing : greyDecal);
      this.spawnGlowMaterial.opacity = alive ? 0.35 : 0.1;
    }
    if (this.baseMaterial) {
      this.baseMaterial.color.copy(alive ? this.baseBaseColor : greyDecal);
      this.baseMaterial.emissiveIntensity = alive ? 0.55 : 0.1;
    }
    if (this.basePadMaterial) {
      this.basePadMaterial.color.copy(alive ? this.baseBaseColor : greyDecal);
      this.basePadMaterial.opacity = alive ? 0.35 : 0.1;
    }
  }

  dispose(): void {
    if (this.highlightMesh) this.target.remove(this.highlightMesh);
    this.target.remove(this.group);
    this.target.remove(this.pathGroup);
    this.target.remove(this.fxGroup);
    while (this.pathGroup.children.length > 0) {
      const child = this.pathGroup.children.pop()!;
      if ((child as THREE.Line).geometry) (child as THREE.Line).geometry.dispose();
    }
    for (const p of this.activePulses) {
      p.mesh.geometry.dispose();
      (p.mesh.material as THREE.Material).dispose();
    }
    this.activePulses.length = 0;
    if (this.pathMaterial) {
      this.pathMaterial.dispose();
      this.pathMaterial = null;
    }
    for (const geo of this.ownedGeometries) geo.dispose();
    this.ownedGeometries.length = 0;
    for (const mat of this.ownedMaterials) mat.dispose();
    this.ownedMaterials.length = 0;
    this.highlightMesh = null;
    this.rangePreview = null;
    this.gridLines = null;
    this.spawnDecals.length = 0;
    this.baseMeshes.length = 0;
  }
}
